import React, { useEffect, useMemo, useState, useRef } from 'react'
import { connect } from 'react-redux'
import { makeStyles, useMediaQuery } from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import StarIcon from '@material-ui/icons/Star'
import StarBorderIcon from '@material-ui/icons/StarBorder'

import { studentCourseActions } from '../../_state/actions'
import { makeGetRequest } from '../../_state/helpers/api'
import { configWithAuth } from '../../_state/helpers'
import { CapeeshColors } from '../../assets/ColorPalette'
import { history } from '../../_state/helpers'
import { useIntl } from 'react-intl'
import { studentScreenMessages } from './StudentScreen.i18n.Messages'

import StudentTopBar from './Components/StudentTopBar'
import CapeeshPlayer from './Components/CapeeshPlayer'

function configWithAuthBlob() {
    const config = configWithAuth()
    config.responseType = 'blob'
    return config
}

const useStyles = makeStyles((theme) => ({
    // ── Full-screen container (accounts for 60px StudentTopBar) ──
    root: {
        height: 'calc(100vh - 60px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: `linear-gradient(180deg, ${CapeeshColors.Primary600} 0%, ${CapeeshColors.BlueBright} 100%)`,
        position: 'relative',
        overflow: 'hidden',
    },

    // ── Back button — floats top-left above content ─
    backButton: {
        position: 'absolute',
        top: 12,
        left: 12,
        zIndex: 10,
        color: '#FFFFFF',
        textTransform: 'none',
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 500,
        borderRadius: 20,
        padding: '4px 14px 4px 8px',
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
        },
    },
    backIcon: {
        fontSize: 18,
        marginRight: 4,
    },

    // ── Loading state ───────────────────────────────
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        flex: 1,
    },
    loadingText: {
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 400,
        color: 'rgba(255, 255, 255, 0.85)',
    },

    // ── Brief layout (mobile-first) ─────────────────
    briefWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: 480,
        padding: '80px 20px 0 20px',
        gap: 20,
        flex: 1,
        boxSizing: 'border-box',
        '@media (max-width: 599px)': {
            padding: '64px 12px 0 12px',
            overflowY: 'auto',
            paddingBottom: 80,
            gap: 16,
        },
    },
    briefWrapperDesktop: {
        maxWidth: 560,
    },
    briefWrapperLandscape: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        maxWidth: 860,
        gap: 24,
        overflowY: 'auto',
        paddingBottom: 40,
    },
    heroImageContainerLandscape: {
        flex: '0 0 40%',
        maxHeight: '50vh',
    },
    briefRightColumn: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 20,
        minWidth: 0,
    },
    startButtonInline: {
        flex: '0 0 100%',
        display: 'flex',
        justifyContent: 'center',
        marginTop: 8,
        '& > button': {
            maxWidth: 440,
        },
    },

    // ── Hero image ──────────────────────────────────
    heroImageContainer: {
        width: '100%',
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.25)',
        position: 'relative',
        aspectRatio: '16 / 10',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        boxSizing: 'border-box',
        '@media (max-width: 599px)': {
            aspectRatio: '16 / 9',
            borderRadius: 14,
        },
    },
    heroImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        opacity: 0,
        transition: 'opacity 600ms ease-in-out',
    },
    heroImageLoaded: {
        opacity: 1,
    },

    // ── Brief content card ──────────────────────────
    briefCard: {
        width: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        borderRadius: 20,
        padding: '24px 24px 20px 24px',
        backdropFilter: 'blur(12px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        boxSizing: 'border-box',
        '@media (max-width: 599px)': {
            borderRadius: 14,
            padding: '16px 16px 14px 16px',
        },
    },
    briefName: {
        fontFamily: 'Rubik',
        fontSize: 22,
        fontWeight: 600,
        lineHeight: '130%',
        color: '#FFFFFF',
        textAlign: 'center',
        '@media (max-width: 599px)': { fontSize: 19 },
    },
    briefDescription: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 400,
        lineHeight: '160%',
        color: 'rgba(255, 255, 255, 0.85)',
        textAlign: 'center',
    },
    briefText: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 400,
        lineHeight: '180%',
        color: '#FFFFFF',
        textAlign: 'left',
        width: '100%',
        whiteSpace: 'pre-wrap',
    },
    bulletList: {
        listStyleType: 'disc',
        paddingLeft: 20,
        margin: 0,
        width: '100%',
        boxSizing: 'border-box',
    },
    bulletItem: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 400,
        lineHeight: '200%',
        color: '#FFFFFF',
    },

    // ── Stars row ───────────────────────────────────
    starsRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 4,
        marginTop: 4,
    },
    starFilled: {
        fontSize: 28,
        color: CapeeshColors.YellowBright,
    },
    starEmpty: {
        fontSize: 28,
        color: 'rgba(255, 255, 255, 0.4)',
    },

    // ── Progress bar ────────────────────────────────
    progressSection: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        boxSizing: 'border-box',
    },
    progressBarBg: {
        width: '70%',
        height: 8,
        borderRadius: 4,
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        overflow: 'hidden',
        position: 'relative',
    },
    progressBarFill: {
        height: '100%',
        borderRadius: 4,
        transition: 'width 600ms ease-in-out',
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
    },
    progressMarkers: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '70%',
    },
    progressMarkerLabel: {
        fontFamily: 'Rubik',
        fontSize: 10,
        fontWeight: 500,
        color: 'rgba(255, 255, 255, 0.6)',
    },

    // ── Start button — fixed 20px from bottom, floats above content ──
    startButtonContainer: {
        position: 'fixed',
        bottom: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 40px)',
        maxWidth: 440,
        zIndex: 20,
    },
    startButton: {
        width: '100%',
        padding: '16px 0',
        borderRadius: 28,
        backgroundColor: '#FFFFFF',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'Rubik',
        fontSize: 17,
        fontWeight: 600,
        color: CapeeshColors.Primary600,
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
        transition: 'transform 150ms ease, box-shadow 150ms ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.2)',
        },
        '&:active': {
            transform: 'translateY(0)',
        },
        '&:disabled': {
            cursor: 'default',
            transform: 'none',
        },
    },
}))

// ─── Helpers ──────────────────────────────────────────────────────

/**
 * Extract all unique image and audio URLs from a lesson's scenario nodes.
 */
function extractAssetUrls(lesson) {
    const images = new Set()
    const audios = new Set()

    if (!lesson) return { images: [], audios: [] }

    // Lesson-level image
    if (lesson.imageUrl) images.add(lesson.imageUrl)

    const nodes = lesson.scenarioNodes || []
    nodes.forEach((node) => {
        if (!node) return

        // Node-level assets
        if (node.backgroundImageUrl) images.add(node.backgroundImageUrl)
        if (node.backgroundAudioUrl) audios.add(node.backgroundAudioUrl)
        if (node.sfxAudioUrl) audios.add(node.sfxAudioUrl)

        // Foreground images
        if (node.scenarioForegroundImages) {
            node.scenarioForegroundImages.forEach((img) => {
                if (img?.imageUrl) images.add(img.imageUrl)
            })
        }

        // Conversation options
        if (node.options) {
            node.options.forEach((opt) => {
                if (opt?.textAudioUrl) audios.add(opt.textAudioUrl)
            })
        }

        // Texts
        if (node.texts) {
            node.texts.forEach((txt) => {
                if (txt?.textAudioUrl) audios.add(txt.textAudioUrl)
            })
        }

        // Test options
        if (node.testOptions) {
            node.testOptions.forEach((to) => {
                if (to?.imageUrl) images.add(to.imageUrl)
                if (to?.textAudioUrl) audios.add(to.textAudioUrl)
                if (to.alternatives) {
                    to.alternatives.forEach((alt) => {
                        if (alt?.imageUrl) images.add(alt.imageUrl)
                        if (alt?.textAudioUrl) audios.add(alt.textAudioUrl)
                    })
                }
            })
        }
    })

    // "Could you repeat that" audio
    if (lesson.couldYouRepeatThatDtos) {
        lesson.couldYouRepeatThatDtos.forEach((c) => {
            if (c?.textAudioUrl) audios.add(c.textAudioUrl)
        })
    }

    return {
        images: Array.from(images),
        audios: Array.from(audios),
    }
}

/**
 * Find the Brief node (scenarioNodeType === 6) in the lesson.
 */
function findBriefNode(lesson) {
    if (!lesson?.scenarioNodes) return null
    return lesson.scenarioNodes.find((n) => n && n.scenarioNodeType === 6) || null
}

/**
 * Extract display-text lines from the lesson-level brief string.
 * The text may use '##' as a bullet separator. We normalise to
 * newlines, split, and return non-empty trimmed lines.
 */
function extractBriefLines(rawText) {
    if (!rawText) return []

    const bulletPointsHashes = rawText.replaceAll('##', '\n')

    return bulletPointsHashes
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
}

// ─── Component ────────────────────────────────────────────────────

const StudentLesson = (props) => {
    // Layout passes computedMatch (not match), location, history, dispatch
    const { dispatch, studentCourse, computedMatch, location } = props
    const classes = useStyles()
    const isDesktop = useMediaQuery('(min-width: 768px)')
    const isWideLandscape = useMediaQuery('(min-width: 1024px) and (orientation: landscape)')
    const intl = useIntl()

    // Route params — computedMatch comes from StudentPrivateLayout
    const customLessonId = computedMatch?.params?.customLessonId || null

    // Navigation state passed from TopicDetailView
    const navState = location?.state || {}

    // Fall back to Redux state if location.state was lost (e.g. page refresh)
    const courseGroupId = navState.courseGroupId || studentCourse?.currentCourseGroupId || null
    const l1Id = navState.l1Id || studentCourse?.currentL1Id || null
    const topicId = navState.topicId || studentCourse?.currentTopicId || null
    const generationIndex = navState.generationIndex
    const isNativeSpeaker = navState.isNativeSpeaker || false

    const { currentLesson, fetchingLesson } = studentCourse || {}

    // ─── Player state ───────────────────────────────────────────
    const [isPlaying, setIsPlaying] = useState(false)

    // ─── Local blob caches ──────────────────────────────────────
    const [imageMap, setImageMap] = useState({})
    const [audioMap, setAudioMap] = useState({})
    const fetchingRef = useRef({})
    const [heroImageLoaded, setHeroImageLoaded] = useState(false)

    const fetchBlob = (assetUrl, kind) => {
        if (!assetUrl) return
        if (fetchingRef.current[assetUrl]) return
        fetchingRef.current[assetUrl] = true

        makeGetRequest(assetUrl, configWithAuthBlob())
            .then((response) => {
                const blobUrl = URL.createObjectURL(response.data)
                console.log(`[fetchBlob] ${kind} | ${assetUrl} | ${response.data.size} bytes | ${blobUrl}`)
                if (kind === 'image') {
                    setImageMap((prev) => ({ ...prev, [assetUrl]: blobUrl }))
                } else {
                    setAudioMap((prev) => ({ ...prev, [assetUrl]: blobUrl }))
                }
            })
            .catch((err) => {
                console.warn(`[StudentLesson] Failed to fetch ${kind}:`, assetUrl, err)
            })
            .finally(() => {
                delete fetchingRef.current[assetUrl]
            })
    }

    // ─── Step 1: Fetch lesson on mount ─────────────────────────
    useEffect(() => {
        if (customLessonId && courseGroupId && l1Id && topicId) {
            dispatch(
                studentCourseActions.getLesson(courseGroupId, l1Id, topicId, customLessonId, generationIndex)
            )
        }
    }, [customLessonId, courseGroupId, l1Id, topicId])

    // ─── Step 2: When lesson loads, fetch brief image first, then all
    useEffect(() => {
        if (!currentLesson) return

        setHeroImageLoaded(false)

        const brief = findBriefNode(currentLesson)

        // Priority: brief background image
        if (brief?.backgroundImageUrl) {
            fetchBlob(brief.backgroundImageUrl, 'image')
        }
        // Lesson-level image
        if (currentLesson.imageUrl) {
            fetchBlob(currentLesson.imageUrl, 'image')
        }

        // Then all remaining assets
        const { images, audios } = extractAssetUrls(currentLesson)
        images.forEach((url) => fetchBlob(url, 'image'))
        audios.forEach((url) => fetchBlob(url, 'audio'))
    }, [currentLesson])

    // ─── Derived state ─────────────────────────────────────────
    const briefNode = useMemo(() => findBriefNode(currentLesson), [currentLesson])

    const briefImageUrl = briefNode?.backgroundImageUrl || currentLesson?.imageUrl || null
    const briefImageBlobUrl = briefImageUrl ? imageMap[briefImageUrl] || null : null

    const lessonName = (isNativeSpeaker && currentLesson?.l2Name) || currentLesson?.name || ''
    const lessonDescription = (isNativeSpeaker && currentLesson?.l2ShortDescription) || currentLesson?.shortDescription || ''
    const rawBrief = (isNativeSpeaker && currentLesson?.l2Brief) || currentLesson?.brief
    const briefLines = useMemo(() => extractBriefLines(rawBrief), [rawBrief])

    // Asset loading progress
    const { images: allImages, audios: allAudios } = useMemo(() => extractAssetUrls(currentLesson), [currentLesson])
    const totalAssets = allImages.length + allAudios.length
    const loadedAssets = useMemo(() => {
        let count = 0
        allImages.forEach((url) => {
            if (imageMap[url]) count++
        })
        allAudios.forEach((url) => {
            if (audioMap[url]) count++
        })
        return count
    }, [allImages, allAudios, imageMap, audioMap])

    const assetsReady = totalAssets > 0 && loadedAssets >= totalAssets
    const assetProgress = totalAssets > 0 ? Math.round((loadedAssets / totalAssets) * 100) : 0

    // Stars from navigation state
    const stars = navState.stars ?? 0
    const maxStars = 3
    const normalizedHighScore = navState.normalizedHighScore ?? 0

    const handleGoToRoot = () => {
        history.push('/student/')
    }

    const handleStartLesson = () => {
        setIsPlaying(true)
    }

    const handleClosePlayer = () => {
        setIsPlaying(false)
    }

    // ─── Top bar (shared across all states) ───────────────────
    const topBar = <StudentTopBar dispatch={dispatch} onGoToRoot={handleGoToRoot} isInMobileView={false} />

    // ─── Player mode ────────────────────────────────────────────
    if (isPlaying && currentLesson) {
        return (
            <CapeeshPlayer
                scenarioV1Dto={currentLesson}
                imageMap={imageMap}
                audioMap={audioMap}
                onClose={handleClosePlayer}
                dispatch={dispatch}
                courseGroupId={courseGroupId}
                l1Id={l1Id}
                topicId={topicId}
                customLessonId={customLessonId}
                generationIndex={generationIndex}
                isNativeSpeaker={isNativeSpeaker}
            />
        )
    }

    // ─── Loading / fetching state ─────────────────────────────
    const isLoading = fetchingLesson || !currentLesson

    if (isLoading) {
        return (
            <React.Fragment>
                {topBar}
                <div className={classes.root}>
                    <Button className={classes.backButton} onClick={handleGoToRoot}>
                        <ArrowBackIcon className={classes.backIcon} />
                        {intl.formatMessage(studentScreenMessages.back)}
                    </Button>
                    <div className={classes.loadingContainer}>
                        <CircularProgress size={48} style={{ color: 'rgba(255,255,255,0.8)' }} />
                        <Typography className={classes.loadingText}>{intl.formatMessage(studentScreenMessages.loadingLesson)}</Typography>
                    </div>
                </div>
            </React.Fragment>
        )
    }

    // ─── Brief view ────────────────────────────────────────────
    const wrapperClass = `${classes.briefWrapper} ${
        isWideLandscape ? classes.briefWrapperLandscape : isDesktop ? classes.briefWrapperDesktop : ''
    }`

    const heroClass = `${classes.heroImageContainer} ${
        isWideLandscape ? classes.heroImageContainerLandscape : ''
    }`

    const heroImageEl = (
        <div className={heroClass}>
            {briefImageBlobUrl && (
                <img
                    src={briefImageBlobUrl}
                    className={`${classes.heroImage} ${heroImageLoaded ? classes.heroImageLoaded : ''}`}
                    alt={lessonName}
                    onLoad={() => setHeroImageLoaded(true)}
                />
            )}
            {!briefImageBlobUrl && briefImageUrl && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                    }}>
                    <CircularProgress size={24} style={{ color: 'rgba(255,255,255,0.4)' }} />
                </div>
            )}
        </div>
    )

    const briefContentEls = (
        <>
            <div className={classes.briefCard}>
                <Typography className={classes.briefName}>{lessonName}</Typography>

                {lessonDescription && (
                    <Typography className={classes.briefDescription}>{lessonDescription}</Typography>
                )}

                {briefLines.length > 0 && (
                    <ul className={classes.bulletList}>
                        {briefLines.map((line, idx) => (
                            <li key={idx} className={classes.bulletItem}>
                                {line}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className={classes.starsRow}>
                {Array.from({ length: maxStars }).map((_, i) =>
                    i < stars ? (
                        <StarIcon key={i} className={classes.starFilled} />
                    ) : (
                        <StarBorderIcon key={i} className={classes.starEmpty} />
                    )
                )}
            </div>

            <div className={classes.progressSection}>
                <div className={classes.progressBarBg}>
                    <div
                        className={classes.progressBarFill}
                        style={{ width: `${Math.round(normalizedHighScore * 100)}%` }}
                    />
                </div>
                <div className={classes.progressMarkers}>
                    <span className={classes.progressMarkerLabel}>70%</span>
                    <span className={classes.progressMarkerLabel}>80%</span>
                    <span className={classes.progressMarkerLabel}>90%</span>
                </div>
            </div>
        </>
    )

    const startButtonEl = (
        <button
            className={classes.startButton}
            onClick={handleStartLesson}
            disabled={!assetsReady && totalAssets > 0}>
            {!assetsReady && totalAssets > 0 ? (
                <>
                    <CircularProgress size={20} style={{ color: CapeeshColors.Primary600 }} />
                    <span>{intl.formatMessage(studentScreenMessages.loadingProgress, { progress: assetProgress })}</span>
                </>
            ) : (
                intl.formatMessage(studentScreenMessages.startLesson)
            )}
        </button>
    )

    return (
        <React.Fragment>
            {topBar}
            <div className={classes.root}>
                <Button className={classes.backButton} onClick={handleGoToRoot}>
                    <ArrowBackIcon className={classes.backIcon} />
                    {intl.formatMessage(studentScreenMessages.back)}
                </Button>

                <div className={wrapperClass}>
                    {heroImageEl}

                    {isWideLandscape ? (
                        <div className={classes.briefRightColumn}>
                            {briefContentEls}
                        </div>
                    ) : (
                        briefContentEls
                    )}

                    {isWideLandscape && (
                        <div className={classes.startButtonInline}>
                            {startButtonEl}
                        </div>
                    )}
                </div>

                {!isWideLandscape && (
                    <div className={classes.startButtonContainer}>
                        {startButtonEl}
                    </div>
                )}
            </div>
        </React.Fragment>
    )
}

const mapStateToProps = (state) => {
    const { studentCourse } = state
    return {
        studentCourse,
    }
}

export default connect(mapStateToProps)(StudentLesson)
